import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "services/api";
import FlexBetween from "components/FlexBetween";
import Header from "components/Header";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  MenuItem,
} from "@mui/material";
import { useGetDashboardQuery } from "state/api";

const RemisesList = () => {
  const theme = useTheme();
  const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { isLoading } = useGetDashboardQuery();
  const [consommations, setConsommations] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [totalDifference, setTotalDifference] = useState(0);
  const [formattedData, setFormattedData] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");  // New state for month filter
  const [yearFilter, setYearFilter] = useState(""); // New state for year filter

  const calculateTotals = useCallback((data, monthFilter, yearFilter) => {
    let filteredData = data;
  
    // Filter out items with valid recetteDate only for calculating totalDifference
    filteredData = filteredData.filter((item) => {
      return item.recetteDate && item.recetteDate.split('-').length === 3; // Check if recetteDate is valid
    });
  
    // Apply month and year filter for total difference calculation
    if (monthFilter) {
      filteredData = filteredData.filter((item) => {
        const recetteDate = new Date(item.recetteDate.split('-').reverse().join('-'));
        return recetteDate.getMonth() + 1 === monthFilter; // Check if month matches
      });
    }
  
    if (yearFilter) {
      filteredData = filteredData.filter((item) => {
        const recetteDate = new Date(item.recetteDate.split('-').reverse().join('-'));
        return recetteDate.getFullYear() === yearFilter; // Check if year matches
      });
    }
  
    // Calculate the total paid and total difference (only for records with valid recetteDate)
    const totalPaid = filteredData.reduce(
      (sum, record) => sum + (record.paidAmount || 0),
      0
    );
  
    const totalDiff = filteredData.reduce(
      (sum, record) => sum + ((record.paidAmount || 0) - (record.duePayment || 0)),
      0
    );
  
    setTotalPaidAmount(totalPaid.toFixed(2));
    setTotalDifference(totalDiff.toFixed(2));
  }, []); // No dependencies because totals calculation is independent of filters for display
  
  const applyFilters = useCallback(
    (data) => {
      const parseFrenchDate = (dateStr) => {
        const [day, month, year] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day); // Creates a Date object (month is 0-based)
      };
  
      let filteredDataForDisplay = data;
  
      // Apply month and year filter for table display
      if (monthFilter || yearFilter) {
        filteredDataForDisplay = filteredDataForDisplay.filter((item) => {
          if (!item.recetteDate) return false; // Skip items without valid recetteDate
  
          const recetteDate = parseFrenchDate(item.recetteDate); // Assuming recetteDate is the date field
          const itemMonth = recetteDate.getMonth() + 1; // Month is 0-based, so we add 1
          const itemYear = recetteDate.getFullYear(); // Get the full year
  
          // Log the date and year to verify correct parsing
          console.log('RecetteDate:', item.recetteDate, 'Year:', itemYear);
  
          // Apply month and year filters
          const monthMatches = monthFilter ? itemMonth === monthFilter : true;
          const yearMatches = yearFilter ? itemYear === yearFilter : true;
  
          return monthMatches && yearMatches;
        });
      }
  
      setConsommations(filteredDataForDisplay);
  
      // Call calculateTotals only for totals, not affecting the table filtering
      calculateTotals(data, monthFilter, yearFilter);
    },
    [calculateTotals, monthFilter, yearFilter] // Dependencies for filtering
  );
      

  useEffect(() => {
    // Apply filters specifically for the table consumption changes
    applyFilters(formattedData);
  }, [formattedData, applyFilters]); // Only dependencies for table filtering
  

  useEffect(() => {
    const fetchConsommations = async () => {
      try {
        const response = await api.get(
          "/api/consommations"
        );
        let data = response.data.map((item) => ({
          ...item,
          paidAmount: Number(item.paidAmount) || 0,
          duePayment: Number(item.duePayment) || 0,
        }));
        data.sort((a, b) => b.id - a.id);
        setFormattedData(data);
        applyFilters(data);
      } catch (error) {
        console.error("Failed to fetch consommations:", error);
      }
    };
  
    fetchConsommations();
  }, [applyFilters]);
  
  const filteredRemiseData = useMemo(() => {
    let data = formattedData;
  
    // Filtrage par mois
    if (monthFilter) {
      data = data.filter((item) => {
        const recetteDate = item.recetteDate;
  
        // Vérifiez que recetteDate est valide
        if (!recetteDate) return false;
  
        const [day, month, year] = recetteDate.split('-');
        const recetteMonth = parseInt(month, 10); // Convertir le mois en nombre
        return recetteMonth === monthFilter;
      });
    }
  
    // Filtrage par année
    if (yearFilter) {
      data = data.filter((item) => {
        const recetteDate = item.recetteDate;
  
        // Vérifiez que recetteDate est valide
        if (!recetteDate) return false;
  
        const [day, month, year] = recetteDate.split('-');
        const recetteYear = parseInt(year, 10); // Convertir l'année en nombre
        return recetteYear === yearFilter;
      });
    }
  
    // Filtrer les enregistrements où duePayment est différent de paidAmount et recetteDate est valide
    return data
      .filter((item) => item.duePayment !== item.paidAmount && item.recetteDate !== null)
      .map((item) => ({
        firstName: item.firstName,
        lastName: item.lastName,
        consommation: item.consommation,
        remise: (item.duePayment - item.paidAmount).toFixed(2),  // Calculer la remise
      }));
  }, [formattedData, monthFilter, yearFilter]);
  

const handleMonthChange = (e) => {
    setMonthFilter(Number(e.target.value)); // Set month filter as number
  };

  const handleYearChange = (e) => {
    setYearFilter(Number(e.target.value)); // Set year filter as number
  };

  return (
    <Box m="1.5rem 2.5rem">
      {/* Header Row */}
      {isMobile && (
        <Box mb={2}>
          <Header
            title="Volet Social"
            subtitle="consommation gratuite"
          />
        </Box>
      )}

      <FlexBetween
        flexDirection={isMobile ? "column" : "row"}
        gap="20px"
        alignItems="center"
        mb={2}
      >
        {!isMobile && (
          <Header
          title="Volet Social"
          subtitle="consommation gratuite"
        />
        )}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap="20px"
          alignItems="center"
        >
          {/* Month Filter */}
          <TextField
  select
  label="Filtrer par mois"
  value={monthFilter}
  onChange={handleMonthChange}
  sx={{ minWidth: "120px" }}
  fullWidth={isMobile}
>
  <MenuItem value="">Tous</MenuItem>
  <MenuItem value={1}>Janvier</MenuItem>
  <MenuItem value={2}>Février</MenuItem>
  <MenuItem value={3}>Mars</MenuItem>
  <MenuItem value={4}>Avril</MenuItem>
  <MenuItem value={5}>Mai</MenuItem>
  <MenuItem value={6}>Juin</MenuItem>
  <MenuItem value={7}>Juillet</MenuItem>
  <MenuItem value={8}>Août</MenuItem>
  <MenuItem value={9}>Septembre</MenuItem>
  <MenuItem value={10}>Octobre</MenuItem>
  <MenuItem value={11}>Novembre</MenuItem>
  <MenuItem value={12}>Décembre</MenuItem>
</TextField>

         {/* Year Filter */}
         <TextField
            select
            label="Filtrer par année"
            value={yearFilter}
            onChange={handleYearChange}
            sx={{ minWidth: "120px" }}
            fullWidth={isMobile}
            >
            <MenuItem value="">Tous</MenuItem>
            {formattedData
                .filter(item => item.recetteDate && item.recetteDate.split('-').length === 3)  // Ensure recetteDate is valid
                .map(item => new Date(item.recetteDate.split('-').reverse().join('-')).getFullYear())
                .filter((year, index, self) => self.indexOf(year) === index)  // Get unique years
                .map((year) => (
                <MenuItem key={year} value={year}>
                    {year}
                </MenuItem>
                ))}
            </TextField>

        </Box>
      </FlexBetween>

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="160px"
        gap="20px"
        sx={{
          "& > div": {
            gridColumn: isNonMediumScreens ? undefined : "span 12",
          },
        }}
      >

        <TotalDifferenceDisplay totalDifference={totalDifference} />

      </Box>
       <Box mt="20px" mb="20px">
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Liste des enregistrements avec remise (Social)
        </Typography>
      
        {filteredRemiseData.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr 1fr', // 3 columns for each field
              gap: 2,
              padding: 2,
              backgroundColor: theme.palette.background.alt,
              borderRadius: '8px',
              boxShadow: 2,
            }}
          >
            {/* Table Headers */}
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Nom
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Consommation
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Remise (CFA)
            </Typography>
      
            {/* Table Data */}
            {filteredRemiseData.map((item, index) => (
              <React.Fragment key={index}>
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  {`${item.firstName} ${item.lastName}`}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  {item.consommation}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.error.main }}>
                  {`${item.remise} CFA`}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
            <Typography variant="body2" sx={{ color: theme.palette.grey[500], fontWeight: 'bold' }}>
              Aucune donnée avec une remise.
            </Typography>
          </Box>
        )}
        </Box>

    </Box>
  );
};


const TotalDifferenceDisplay = ({ totalDifference }) => {
  const theme = useTheme();

  return (
    <Box
      gridColumn="span 4"
      gridRow="span 1"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      marginTop={1}
      marginBottom={1}
      sx={{
        backgroundColor: theme.palette.background.alt,
        borderRadius: "1rem",
        padding: "1rem",
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: theme.palette.secondary[300], mb: 1 }}
      >
        Remise
      </Typography>
      <Typography
        variant="h4"
        sx={{ color: theme.palette.secondary[100] }}
      >
        {totalDifference} CFA
      </Typography>
    </Box>
  );
};

export default RemisesList;

